import { z } from 'zod';
import { FUNDED_BY_NAME_MAX_LENGTH } from '../impact/validation';
import type { ImpactTile } from '../forest/tile';
import type { PublicProject } from '../projects';
import { PAYMENT_TYPE as PAYMENT_TYPE_VALUE } from '../types';
import type { PaymentResponse } from '../types/payment';
import { BASKET_MAX_LINE_ITEMS } from './constants';

export type ImpactShopListingCheckoutResponse = PaymentResponse & {
  orderId: number;
  projects: PublicProject[];
  tiles: ImpactTile[];
};

const checkoutLineBaseSchema = z.object({
  basketItemId: z.string().min(1),
  listingId: z.number().int().positive(),
  fundedBy: z.string().max(FUNDED_BY_NAME_MAX_LENGTH).optional(),
  groupId: z.number().int().positive().optional(),
  recipientEmail: z.string().email().optional(),
});

export const impactShopListingCheckoutLineSchema = z.union([
  checkoutLineBaseSchema.extend({ quantity: z.number().positive(), contributionMinor: z.never().optional() }),
  checkoutLineBaseSchema.extend({
    contributionMinor: z.number().int().positive().safe(),
    quantity: z.never().optional(),
  }),
]);

export const impactShopListingCheckoutPayloadSchema = z
  .object({
    idempotencyKey: z.string().min(1).max(191),
    expectedTotalMinor: z.number().int().positive().safe(),
    paymentType: z.enum([PAYMENT_TYPE_VALUE.CARD, PAYMENT_TYPE_VALUE.INVOICE]),
    poNumber: z.string().max(30).optional(),
    taxCode: z.string().max(30).optional(),
    utmParams: z.record(z.string(), z.string().optional()).optional(),
    items: z
      .array(impactShopListingCheckoutLineSchema)
      .min(1)
      .max(BASKET_MAX_LINE_ITEMS)
      .refine(items => new Set(items.map(item => item.basketItemId)).size === items.length),
  })
  .strict();

export type ImpactShopListingCheckoutLine = z.infer<typeof impactShopListingCheckoutLineSchema>;
export type ImpactShopListingCheckoutPayload = z.infer<typeof impactShopListingCheckoutPayloadSchema>;
